<script setup lang="ts">
import { addYears } from 'date-fns'
import {
  fetchPerformancesData,
  fetchTicketCategoriesData,
  queryKey,
  type InfoData,
} from '~/queries'
import { fetchAvailabilityData } from '~/queries/availability'

type Props = {
  product: Exclude<InfoData['product'], undefined>
  class?: string
}

const props = defineProps<Props>()

const classes = computed(() => ({
  'app-tickets-attraction': true,
  [`${props.class}`]: !!props.class,
}))

const cart = useCartStore()
const site = useSiteStore()
const modals = useModalsStore()

const [ticketCategoriesResult, performancesResult] = await Promise.all([
  useAsyncData(
    queryKey('ticket-categories', props.product?.id),
    async () => {
      const data = await fetchTicketCategoriesData({
        productId: props.product.id,
      })

      return data
    },
    { server: false, lazy: true },
  ),
  useAsyncData(
    queryKey('performances', props.product?.id),
    async () => {
      if (!props.product.hasPerformances) return

      const data = await fetchPerformancesData({ productId: props.product.id })

      return data
    },
    { server: false, lazy: true },
  ),
])

const { data: ticketCategoriesData, pending: ticketCategoriesPending } =
  ticketCategoriesResult

const { data: performancesData, pending: performancesPending } =
  performancesResult

const ticketCategories = computed(() =>
  ticketCategoriesData.value?.items.map((item) => ({
    ...item,
    label: item.title,
    value: item.id,
  })),
)

const performances = computed(() =>
  performancesData.value?.items.map((performance) => ({
    ...performance,
    date: performance.dateTime.split(' ')[0],
  })),
)

const availableDates = computed(() => {
  const dates = performances.value?.map((item) => item.date).sort()

  return [...new Set(dates)].map((date) => new Date(date))
})

const minDate = computed(() => {
  if (props.product.hasPerformances) {
    return availableDates.value[0] ?? new Date()
  }

  return new Date()
})

const maxDate = computed(() => {
  if (props.product.hasPerformances) {
    return availableDates.value[availableDates.value.length - 1] ?? new Date()
  }

  if (props.product.endDate) {
    return new Date(props.product.endDate)
  }

  return addYears(new Date(), 1)
})

const disabledDates = computed(() => {
  if (!props.product.hasPerformances) return

  const startDate = minDate.value
  const endDate = maxDate.value
  const datesArray = []

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    datesArray.push(new Date(d))
  }

  // handle bug with v-calendar disable_dates to open calendar in nearest month
  const dayBeforeMinDate = new Date(minDate.value);
  dayBeforeMinDate.setDate(minDate.value.getDate() - 1)

  const ddates = datesArray.filter(
    (date) =>
      !availableDates.value.some(
        (availableDate) =>
          date.toISOString().split('T')[0] ===
          availableDate.toISOString().split('T')[0],
      ),
  )
  ddates.unshift(dayBeforeMinDate)
  return ddates
})

const timeOptions = computed(
  () =>
    performances.value
      ?.filter((item) => item.date === date.value?.toISOString().split('T')[0])
      .map((item) => ({
        label: formatTime(item.dateTime),
        value: item.id,
        externalId: item.externalId,
      })) ?? [],
)

const quantity = ref<Record<string, number>>({})
const date = ref<Date | null>(null)
const time = ref<(typeof timeOptions.value)[number] | null>(null)
const ticketCategory = ref<
  Exclude<typeof ticketCategories.value, undefined>[number] | null
>(null)

watch(date, () => (time.value = null))

const ticketCategoryId = computed(() => ticketCategory.value?.value)
const performanceId = computed(() => time.value?.value)
const isoDate = computed(() => date.value?.toISOString().split('T')[0])

const { data: availabilityData, pending: availabilityPending } =
  await useAsyncData(
    queryKey(
      'availability',
      props.product?.id,
      performanceId.value,
      isoDate.value,
    ),
    async () => {
      if (
        (props.product.hasPerformances && !performanceId.value) ||
        !isoDate.value
      )
        return

      const data = await fetchAvailabilityData({
        productId: props.product.id,
        performanceId: performanceId.value,
        date: isoDate.value,
      })

      return data
    },
    { server: false, lazy: true, watch: [performanceId, isoDate] },
  )

const participants = computed(() => {
  if (
    !availabilityData.value ||
    !props.product.variations ||
    !isoDate.value ||
    (props.product.hasPerformances && !performanceId.value)
  )
    return

  if (ticketCategories.value?.length) {
    return availabilityData.value.items
      .filter((item) => ticketCategoryId.value === item.ticketCategory?.id)
      .map((item) => {
        const variation = props.product.variations?.find(
          (variation) => variation.externalId === item.variationId,
        )

        return {
          ...item,
          ...variation,
        }
      })
      .filter((item) => item.name && item.externalId)
  }

  return availabilityData.value.items.map((item) => {
    const variation = props.product.variations?.find(
      (variation) => variation.externalId === item.variationId,
    )

    return {
      ...item,
      ...variation,
    }
  })
})

watch(participants, (value) => {
  if (!value) {
    quantity.value = {}
    return
  }

  quantity.value = value.reduce((acc, item) => {
    return {
      ...acc,
      [item.variationId]: quantity.value[item.variationId] || 0,
    }
  }, {})
})

const total = computed(() => {
  return Object.keys(quantity.value).reduce((acc, variationId) => {
    const participant = participants.value?.find(
      (participant) => participant.variationId === variationId,
    )

    if (!participant) return acc

    return acc + participant.price * quantity.value[variationId]
  }, 0)
})

const hasTickets = computed(() => {
  const now = new Date()

  return maxDate.value >= now
})

const submitting = ref(false)
</script>

<template>
  <AppPaper :class="classes" class="relative p-3 pt-6 md:p-16">
    <template v-if="hasTickets">
      <div class="row relative z-20 mb-6">
        <div
          class="mb-6"
          :class="[product.hasPerformances ? 'col-md-6' : 'col-md-12']"
        >
          <h3>{{ $t('string.Date') }}</h3>

          <AppSelectDate
            v-model="date"
            :label="$t('string.Add-date')"
            full-width
            :disabled-dates="disabledDates"
            :min-date="minDate"
            :max-date="maxDate"
          />
        </div>

        <div v-if="product.hasPerformances" class="col-md-6">
          <h3>{{ $t('string.Time') }}</h3>

          <AppSelect
            v-model="time"
            class="w-full"
            :title="$t('string.Add-time')"
            :options="timeOptions"
            full-width
            :class="{
              'pointer-events-none opacity-50':
                !date || !product.hasPerformances,
            }"
          ></AppSelect>
        </div>
      </div>

      <template v-if="ticketCategories?.length">
        <h3>{{ $t('component.tickets.attraction.category.label') }}</h3>

        <AppSelect
          v-model="ticketCategory"
          class="relative z-10 mb-6 w-full"
          :title="$t('component.tickets.attraction.category.placeholder')"
          :options="ticketCategories"
          full-width
        ></AppSelect
      ></template>

      <template
        v-if="
          (ticketCategories?.length ? ticketCategoryId : true) && participants
        "
      >
        <h3>{{ $t('component.tickets.attraction.participants.label') }}</h3>

        <section>
          <header>
            <p>
              {{ $t('component.tickets.attraction.participants.description') }}
            </p>
          </header>

          <div class="p-4">
            <template
              v-for="participant in participants"
              :key="participant.variationId"
            >
              <article class="app-tickets-attraction__category">
                <header>
                  <h4>{{ participant.name }}</h4>

                  <p v-if="participant.description">
                    {{ participant.description }}
                  </p>
                </header>

                <p class="app-tickets-attraction__category__price">
                  {{ formatCurrency(participant.price) }}
                </p>

                <AppInputQuantity v-model="quantity[participant.variationId]" />
              </article>

              <AppDivider class="my-3" />
            </template>

            <footer class="app-tickets-attraction__total">
              <h4>{{ $t('string.Total') }}:</h4>

              <p>{{ formatCurrency(total) }}</p>
            </footer>
          </div>
        </section>
      </template>

      <AppButton
        full-width
        class="mt-8"
        :loading="submitting"
        :disabled="
          total <= 0 || (product.hasPerformances && !performanceId) || !isoDate
        "
        @click="
          async () => {
            submitting = true

            await cart.addToCart({
              productId: product.id,
              ...(product.hasPerformances
                ? { performanceId }
                : { dateTime: `${isoDate} 00:00:00` }),
              ticketCategoryId: ticketCategoryId,
              variations: Object.entries(quantity)
                .filter(([_id, quantity]) => quantity >= 1)
                .map(([id, quantity]) => ({ id, quantity })),
            })

            submitting = false
          }
        "
      >
        {{ $t('component.tickets.attraction.button') }}
      </AppButton>

      <Transition name="fade">
        <div
          v-if="
            availabilityPending ||
            ticketCategoriesPending ||
            performancesPending
          "
          class="text-red absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-white/75"
        >
          <AppSpinner :style="{ fontSize: '2.5rem' }" />
        </div>
      </Transition>
    </template>
    <div v-else class="p-3 pt-6 md:p-6">
      <div v-if="product?.soldOut?.message" class="pb-4 reset-list-styles" v-html="product.soldOut.message"/>
      <p
        v-else
        v-html="$t('page.product.unavailable.description', {
      email: site.data?.emailAddress,
    })"
      />
      <AppButton v-if="product?.soldOut?.modal" full-width @click="modals.openContactUsModal()">{{ $t('page.faqs.extra.contact.title') }}</AppButton>
    </div>
  </AppPaper>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-tickets-attraction {
  h3 {
    margin: 0 0 0.75rem;
    font-family: $font-barlow;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.42857143;
    letter-spacing: 0.10714286em;
    text-transform: uppercase;
  }

  section {
    border: 1px solid $black;
    border-radius: 0.125rem;

    & > header {
      padding: 1rem;
      border-bottom: 1px solid $black;
    }
  }
}

.app-tickets-attraction__category {
  display: flex;
  align-items: center;

  header {
    p {
      margin: 0;
      font-family: $font-barlow;
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 1.33333333;
      letter-spacing: 0.08333333em;
      text-transform: uppercase;
    }
  }

  h4 {
    margin: 0;
    font-size: inherit;
    line-height: 1.5;
    font-weight: 500;
    color: $black;
  }

  &__price {
    font-weight: 500;
    line-height: 1.5;
    color: $black;
    margin: 0 0.75rem 0 auto;
  }
}

.app-tickets-attraction__total {
  display: flex;
  justify-content: space-between;

  h4,
  p {
    margin: 0;
    font-size: 1.25rem;
    line-height: 1.5;
    font-weight: 500;
    color: $black;
  }
}
</style>
